import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Send } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { formatBytes, formatRelative } from '@/lib/format';
import { FolderPicker } from './FolderPicker';

/**
 * Dialog TERIMA: membuat kode sekali pakai dan memilih folder pendaratannya.
 *
 * Arahnya sengaja dibalik: penerima yang membuat kode, bukan pengirim.
 * Dengan begitu tidak ada yang bisa menjejalkan berkas ke penyimpanan orang
 * tanpa kode buatannya sendiri, dan kirimannya mendarat di folder pilihannya.
 */
export const ReceiveDialog = ({ open, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [folderId, setFolderId] = useState('');
  const [tersalin, setTersalin] = useState(null);

  const { data } = useQuery({
    queryKey: ['transfer-codes'],
    queryFn: () => api.get('/storage/transfer/codes'),
    enabled: open,
    // Kode aktif dipantau: begitu pengirim memakainya, statusnya berubah.
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some((k) => !k.usedAt) ? 3000 : false,
  });

  const buat = useMutation({
    mutationFn: () => api.post('/storage/transfer/codes', { folderId: folderId || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['transfer-codes'] }),
    onError: (err) =>
      toast.error('Gagal membuat kode', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const salin = async (kode) => {
    await navigator.clipboard.writeText(kode).catch(() => {});
    setTersalin(kode);
    setTimeout(() => setTersalin(null), 1500);
  };

  const daftar = data?.data ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Terima Berkas"
      description="Buat kode, kirimkan ke pengirimnya. Begitu dipakai, berkas muncul di folder pilihan Anda."
    >
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Folder pendaratan">
              {(props) => <FolderPicker {...props} value={folderId} onChange={setFolderId} />}
            </Field>
          </div>
          <Button loading={buat.isPending} onClick={() => buat.mutate()}>
            Buat Kode
          </Button>
        </div>

        {daftar.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
              Kode Anda
            </p>
            {daftar.slice(0, 5).map((k) => (
              <div
                key={k.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border-subtle)] px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm font-bold text-[var(--text-primary)]">
                    {k.code}
                  </code>
                  {k.usedAt ? (
                    <Badge tone="success" size="sm">
                      terpakai: {k.receivedFiles} berkas, {formatBytes(k.receivedBytes)}
                    </Badge>
                  ) : new Date(k.expiresAt) < new Date() ? (
                    <Badge tone="neutral" size="sm">
                      kedaluwarsa
                    </Badge>
                  ) : (
                    <Badge tone="info" size="sm">
                      aktif sampai {formatRelative(k.expiresAt)}
                    </Badge>
                  )}
                </div>
                {!k.usedAt && (
                  <Button
                    variant="ghost"
                    size="xs"
                    icon={tersalin === k.code ? Check : Copy}
                    onClick={() => salin(k.code)}
                  >
                    {tersalin === k.code ? 'Tersalin' : 'Salin'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

/**
 * Dialog KIRIM: masukkan kode penerima, centang berkas atau folder yang mau
 * dikirim. Batas total 100 MB ditegakkan server; totalnya ditampilkan di sini
 * supaya penolakan tidak pernah jadi kejutan.
 */
export const SendDialog = ({ open, onClose }) => {
  const toast = useToast();
  const [kode, setKode] = useState('');
  const [pilihanBerkas, setPilihanBerkas] = useState({});
  const [pilihanFolder, setPilihanFolder] = useState({});

  const { data: fileData } = useQuery({
    queryKey: ['transfer-files'],
    queryFn: () => api.get('/storage/files', { params: { source: '', limit: 100 } }),
    enabled: open,
  });
  const { data: folderData } = useQuery({
    queryKey: ['storage-folders-all'],
    queryFn: () => api.get('/storage/folders/all'),
    enabled: open,
  });

  const files = fileData?.data?.files ?? fileData?.data ?? [];
  const folders = folderData?.data ?? [];

  const fileIds = Object.keys(pilihanBerkas).filter((id) => pilihanBerkas[id]);
  const folderIds = Object.keys(pilihanFolder).filter((id) => pilihanFolder[id]);
  const totalBytes = files
    .filter((f) => pilihanBerkas[f.id])
    .reduce((sum, f) => sum + f.sizeBytes, 0);

  const kirim = useMutation({
    mutationFn: () => api.post('/storage/transfer/send', { code: kode, fileIds, folderIds }),
    onSuccess: (hasil) => {
      toast.success('Terkirim', { description: hasil?.data?.message });
      setKode('');
      setPilihanBerkas({});
      setPilihanFolder({});
      onClose();
    },
    onError: (err) =>
      toast.error('Gagal mengirim', {
        description: err instanceof ApiError ? err.message : 'Periksa kodenya lalu coba lagi.',
      }),
  });

  const barisCentang = (id, label, keterangan, state, setState) => (
    <label
      key={id}
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-3 py-2 transition-colors hover:bg-[var(--surface-sunken)]"
    >
      <input
        type="checkbox"
        checked={Boolean(state[id])}
        onChange={(e) => setState((prev) => ({ ...prev, [id]: e.target.checked }))}
        className="size-4 rounded border-[var(--border-default)]"
      />
      <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">{label}</span>
      <span className="shrink-0 text-xs text-[var(--text-muted)]">{keterangan}</span>
    </label>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Kirim Berkas"
      description="Minta kode dari penerimanya (menu Terima di akun dia), lalu pilih yang mau dikirim. Maksimal 100 MB."
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-xs text-[var(--text-muted)]">
            {fileIds.length + folderIds.length} dipilih
            {totalBytes > 0 && ` · ${formatBytes(totalBytes)}`}
            {folderIds.length > 0 && ' + isi folder'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Batal
            </Button>
            <Button
              icon={Send}
              loading={kirim.isPending}
              disabled={kode.trim().length < 6 || fileIds.length + folderIds.length === 0}
              onClick={() => kirim.mutate()}
            >
              Kirim
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Kode dari penerima" required>
          {(props) => (
            <Input
              {...props}
              value={kode}
              onChange={(e) => setKode(e.target.value.toUpperCase())}
              placeholder="CRW-XXXX-XXXX"
              className="font-mono tracking-wider"
            />
          )}
        </Field>

        {folders.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
              Folder (terkirim beserta isinya)
            </p>
            <div className="max-h-32 space-y-1.5 overflow-y-auto">
              {folders.map((f) =>
                barisCentang(f.id, f.path, 'folder', pilihanFolder, setPilihanFolder),
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Berkas
          </p>
          <div className="max-h-56 space-y-1.5 overflow-y-auto">
            {files.length === 0 ? (
              <p className="py-3 text-center text-sm text-[var(--text-muted)]">
                Penyimpanan Anda masih kosong.
              </p>
            ) : (
              files.map((f) =>
                barisCentang(
                  f.id,
                  f.filename,
                  formatBytes(f.sizeBytes),
                  pilihanBerkas,
                  setPilihanBerkas,
                ),
              )
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
